import { toast } from 'sonner'
import { useForm } from 'react-hook-form'

import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'

import { useManageServiceContacts } from 'src/hooks/useManageServiceContacts'

import { FormDataContact, ServiceType } from 'src/pages/ServiceContacts/types'
import { getFormFields } from 'src/pages/ServiceContacts/helpers'
import { queryClient } from 'src/features/AppProviders/constants'
import { AlertContactUpdateWindowProps } from 'src/features/AlertContactUpdateWindow/types'
import { ServiceContact } from 'src/api/ServiceContacts/types'

export const useAlertContactUpdateWindow = ({
  itemId,
  toggle,
  contactName,
  phoneNumber,
  serviceType,
  address,
  additionalNotes,
  onSave,
}: Omit<AlertContactUpdateWindowProps, 'isOpen'>) => {
  const { translations } = useLocales()

  const { updateContact, isLoadingGeneral } = useManageServiceContacts()

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<FormDataContact>({
    defaultValues: {
      contactName,
      phoneNumber,
      serviceType,
      address,
      additionalNotes,
    },
  })

  const onUpdate = (): void => {
    const { contactName, phoneNumber, serviceType, address, additionalNotes } =
      getValues()

    const updateItem: ServiceContact = {
      contactName: contactName.trim(),
      phoneNumber: phoneNumber.trim(),
      serviceType: serviceType || ServiceType.GENERAL_MAINTENANCE,
      address: address.trim() || '',
      additionalNotes: additionalNotes.trim() || '',
    }

    updateContact(
      { payload: updateItem, id: itemId },
      {
        onSuccess: () => {
          onSave?.({
            id: itemId,
            ...updateItem,
          })

          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.SERVICE_CONTACTS],
          })
          toggle()
          toast(
            translations.serviceContacts.historyServiceContacts.toast.success
              .title,
            {
              description:
                translations.serviceContacts.historyServiceContacts.toast
                  .success.description,
              action: {
                label:
                  translations.serviceContacts.historyServiceContacts.toast
                    .success.action.label,
                onClick: () => toast.dismiss(),
              },
            },
          )
        },
        onError: error => {
          toast(
            translations.serviceContacts.historyServiceContacts.toast.error
              .title,
            {
              description:
                translations.serviceContacts.historyServiceContacts.toast.error
                  .description + error,
              action: {
                label:
                  translations.trackFuel.historyFuel.toast.error.action.label,
                onClick: () => toast.dismiss(),
              },
            },
          )
        },
      },
    )
  }

  return {
    title:
      translations.serviceContacts.historyServiceContacts.alert.update.title,
    cancel: translations.serviceContacts.historyServiceContacts.alert.cancel,
    updateBtn:
      translations.serviceContacts.historyServiceContacts.alert.update
        .updateBtn,
    fields: getFormFields(translations.serviceContacts),
    errors,
    control,
    isLoadingGeneral,
    handleSubmit,
    onUpdate,
  }
}
